import { Tree, TreeNodeInfo } from "@blueprintjs/core";
import _, { cloneDeep, Dictionary } from "lodash";
import { ProductGroup } from "../../api/schema";
import React, { useMemo } from "react";

type Repository = Dictionary<ProductGroup>;

interface ProductGroupTreeProps {
  set_active_product_group: (pg: ProductGroup) => void;
  group_repository: Repository;
  update_product_groups: (pgs: ProductGroup[]) => void;
  fetch_product_group: (id: number) => Promise<ProductGroup>;
}

type fn = () => void;

type TreeAction =
  | {
      type: "SET_IS_EXPANDED";
      id: number;
      isExpanded: boolean;
    }
  | {
      type: "SET_IS_SELECTED";
      id: number;
      isSelected: boolean;
      repo: Repository;
      callback: fn;
    }
  | {
      type: "REBUILD";
      repo: Repository;
    };

function forEachNode(
  nodes: TreeNodeInfo<ProductGroup>[] | undefined,
  callback: (node: TreeNodeInfo<ProductGroup>) => void
) {
  if (nodes === undefined) {
    return;
  }

  for (const node of nodes) {
    callback(node);
    forEachNode(node.childNodes, callback);
  }
}

const findNode = (
  id: number,
  state: TreeNodeInfo<ProductGroup>[]
): TreeNodeInfo<ProductGroup> | null => {
  for (const info of state) {
    if (info.id === id) {
      return info;
    }
  }
  for (const info of state) {
    let n = findNode(id, info.childNodes || []);
    if (n) return n;
  }
  return null;
};

function treeReducer(state: TreeNodeInfo<ProductGroup>[], action: TreeAction) {
  switch (action.type) {
    case "REBUILD": {
      const buildNode = (group: ProductGroup): TreeNodeInfo<ProductGroup> => {
        const existing = findNode(group.id, state);
        let info: TreeNodeInfo<ProductGroup> = {
          id: group.id,
          label: group.name,
          nodeData: group,
          hasCaret: group.children?.length > 0,
          childNodes: _.map(group.children, (c) =>
            buildNode(_.find(action.repo, { id: c.id })!)
          ),
        };
        if (existing) {
          info.isExpanded = existing.isExpanded;
          info.isSelected = existing.isSelected;
          info.disabled = existing.disabled;
        } else {
          if (info.nodeData && info.nodeData.parent === null)
            info.isExpanded = true;
        }
        return info;
      };

      const nodes = _(action.repo)
        .filter((d) => {
          if (d.parent) return false;
          return true;
        })
        .map((group) => buildNode(group))
        .value();

      return nodes;
    }
    // case "DESELECT_ALL":
    //   const newState1 = cloneDeep(state);
    //   forEachNode(newState1, (node) => (node.isSelected = false));
    //   return newState1;
    case "SET_IS_EXPANDED":
      const newState2 = cloneDeep(state);

      forEachNode(
        newState2,
        (n) =>
          (n.isExpanded = n.id === action.id ? action.isExpanded : n.isExpanded)
      );

      return newState2;
    case "SET_IS_SELECTED":
      const newState3 = cloneDeep(state);

      forEachNode(
        newState3,
        (n) => (n.isSelected = n.id === action.id ? action.isSelected : false)
      );

      action.callback();

      return newState3;
    default:
      return state;
  }
}

export const ProductGroupTree = (props: ProductGroupTreeProps) => {
  const [nodes, dispatch] = React.useReducer(treeReducer, []);

  useMemo(() => {
    // dispatch ui update
    dispatch({
      type: "REBUILD",
      repo: props.group_repository,
    });
  }, [props.group_repository, dispatch]);

  const handleNodeClick = React.useCallback(
    async (node: TreeNodeInfo<ProductGroup>) => {
      const originallySelected = node.isSelected;
      dispatch({
        id: node.nodeData!.id,
        isSelected: originallySelected == null ? true : !originallySelected,
        type: "SET_IS_SELECTED",
        repo: props.group_repository,
        callback: async () => {
          let id = node.nodeData!.id;
          let g = _.find(props.group_repository, { id });
          if (!g) {
            g = await props.fetch_product_group(id);
          }
          props.set_active_product_group(g);
          const children = await Promise.all(
            _.map(g.children, (c) => props.fetch_product_group(c.id))
          );

          props.update_product_groups([g, ...children]);
        },
      });
    },
    [props]
  );

  const handleNodeCollapse = React.useCallback(
    (_node: TreeNodeInfo<ProductGroup>) => {
      dispatch({
        id: _node.nodeData!.id,
        isExpanded: false,
        type: "SET_IS_EXPANDED",
      });
    },
    []
  );

  const handleNodeExpand = React.useCallback(
    (_node: TreeNodeInfo<ProductGroup>) => {
      dispatch({
        id: _node.nodeData!.id,
        isExpanded: true,
        type: "SET_IS_EXPANDED",
      });
    },
    []
  );

  return (
    <Tree
      contents={nodes}
      onNodeClick={handleNodeClick}
      onNodeExpand={handleNodeExpand}
      onNodeCollapse={handleNodeCollapse}
    />
  );
};
